import {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Action } from '@reduxjs/toolkit';
import { useFeatureFlags } from '@johnlewispartnership/wtr-website-decisioner-client/express';
import useOnClickOutside from 'hooks/use-on-click-outside';
import { KEY_ESCAPE } from 'constants/keys';
import { MAX_SEARCH_WORDS, MAX_SEARCH_CHARACTERS } from 'constants/search';
import SETTINGS from 'constants/settings';
import { sanitizeString, removeDiacritics } from 'utils/format';
import { limitWords } from 'utils/limit-words';
import { limitCharacters } from 'utils/limit-characters';
import { yieldToMain } from 'utils/yield-to-main';
import { navigateTo } from 'utils/location';
import { getMinSuggestChars } from 'components/Search/utils/get-min-suggest-chars';
import { saveSearchTerm } from 'components/Search/SearchForm/save-search-term';
import { WtrGetState, WtrInferredDispatch } from 'redux/store';
import { useWtrDispatch, WtrDispatch } from 'redux/hooks';

type UpdateSearchTermArgs = {
  searchTerm: string;
  origin: string;
};

interface UseSearchFormProps {
  autoSuggestActive: boolean;
  eligibleForAutoSuggest: boolean;
  functionalCookieConsent: boolean;
  initialValue: string;
  onChange: (searchTerm: string) => (dispatch: WtrDispatch) => void;
  onClear: () => { type: string };
  onSubmit: (
    args: UpdateSearchTermArgs,
  ) => (dispatch: WtrInferredDispatch, getState: WtrGetState) => string;
  onSearch: ((args: boolean) => Action) | null;
  recipes?: boolean;
  url?: (searchTerm: string) => string;
  searchType?: string;
}

export const useSearchForm = ({
  autoSuggestActive,
  eligibleForAutoSuggest,
  functionalCookieConsent,
  initialValue,
  onChange,
  onClear,
  onSearch,
  onSubmit,
  url,
  searchType,
}: UseSearchFormProps) => {
  const dispatch = useWtrDispatch();
  const { searchHistory: searchHistoryEnabled } = useFeatureFlags();
  const inputRef = useRef<HTMLInputElement>(null);
  const insideRef = useRef<HTMLFormElement>(null);

  const [hasFocus, setHasFocus] = useState(false);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [value, setValue] = useState(initialValue);
  const hasValue = !!value?.length;

  useEffect(() => {
    const handleSearchUpdate = (event: Event) => {
      const newSearchTerm = (event as CustomEvent<{ searchTerm: string }>).detail.searchTerm;
      setValue(newSearchTerm);
    };

    window.addEventListener('browse:search:update_searchbar', handleSearchUpdate);

    return () => {
      window.removeEventListener('browse:search:update_searchbar', handleSearchUpdate);
    };
  }, []);

  const focusInput = useCallback((focus: boolean) => {
    const { current: input } = inputRef;
    if (!input) return;

    if (focus) input.focus();
    else input.blur();
  }, []);

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const eventValue = event.target.value;
      const searchTerm = limitCharacters(
        limitWords(sanitizeString(eventValue || '', true), MAX_SEARCH_WORDS),
        MAX_SEARCH_CHARACTERS,
      );
      setValue(searchTerm);

      await yieldToMain();

      const isSameSearchTerm = value === searchTerm;
      const minimumCharacters = getMinSuggestChars;
      setShowSearchHistory(searchTerm.length === 0);

      if (
        eligibleForAutoSuggest &&
        searchTerm.trim().length >= minimumCharacters &&
        !isSameSearchTerm &&
        onChange
      ) {
        dispatch(onChange(removeDiacritics(searchTerm)));
      }

      if (searchTerm.length < 2 && autoSuggestActive && onClear) dispatch(onClear());
    },
    [autoSuggestActive, eligibleForAutoSuggest, dispatch, onChange, onClear, value],
  );

  const handleFocus = useCallback(() => {
    if (!hasFocus) {
      focusInput(true);
      setHasFocus(true);
      setShowSearchHistory(!hasValue);
    }
  }, [focusInput, hasFocus, hasValue]);

  const handleBlur = useCallback(() => {
    if (hasFocus) {
      focusInput(false);
      setHasFocus(false);
      setShowSearchHistory(false);
    }
  }, [focusInput, hasFocus]);

  const handleClear = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (event) event.preventDefault();
      focusInput(true);
      if (onClear) dispatch(onClear());
      setShowSearchHistory(true);
      setValue('');
      setHasFocus(true);
    },
    [focusInput, onClear, dispatch],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === KEY_ESCAPE) {
        handleBlur();
      }
    },
    [handleBlur],
  );

  const searchForTerm = useCallback(
    async ({ searchTerm, origin }: { searchTerm: string; origin: string }) => {
      const trimmedTerm = searchTerm.trim();
      const queryString = dispatch(
        onSubmit({
          searchTerm: removeDiacritics(trimmedTerm),
          origin,
        }),
      );

      setValue(trimmedTerm);

      await yieldToMain();

      if (trimmedTerm !== '') {
        if (onClear) dispatch(onClear());
        navigateTo(url ? url(queryString) : `${SETTINGS.searchPath}?${queryString}`);
      } else {
        navigateTo(SETTINGS.searchPath);
      }

      // TODO - Reinstate SPA navigation when needed
      // historyUtils.push(url ? url(queryString) : `${SETTINGS.searchPath}?${queryString}`);
      // slowlyScrollPageVerticallyToTop();

      handleBlur();

      if (functionalCookieConsent) {
        if (searchHistoryEnabled) saveSearchTerm(trimmedTerm, searchType || '');
      }

      if (onSearch) dispatch(onSearch(false));
    },
    [
      onSubmit,
      dispatch,
      handleBlur,
      functionalCookieConsent,
      onSearch,
      onClear,
      url,
      searchType,
      searchHistoryEnabled,
    ],
  );

  const handleSubmit = useCallback(
    (event: FormEvent) => {
      if (event) event.preventDefault();

      return initialValue !== value
        ? searchForTerm({ searchTerm: value, origin: 'regular' })
        : setHasFocus(false);
    },
    [initialValue, searchForTerm, value],
  );

  const handleSelectedSearchHistoryItem = useCallback(
    (searchTerm: string) => {
      setShowSearchHistory(false);
      setHasFocus(false);
      sessionStorage.setItem('common:search:search_origin', 'search_history');

      return initialValue !== searchTerm
        ? searchForTerm({ searchTerm, origin: 'search history' })
        : setValue(searchTerm);
    },
    [initialValue, searchForTerm],
  );

  const handleSelectedAutoCompleteItem = useCallback(
    (searchTerm: string) => {
      if (initialValue !== searchTerm) {
        sessionStorage.setItem('common:search:search_origin', 'search_recommendation');
        return searchForTerm({ searchTerm, origin: 'auto complete' });
      }
      setHasFocus(false);

      return setValue(searchTerm);
    },
    [initialValue, searchForTerm],
  );

  useOnClickOutside(insideRef, () => {
    handleBlur();
  });

  return {
    handleChange,
    handleClear,
    handleFocus,
    handleKeyDown,
    handleSelectedAutoCompleteItem,
    handleSelectedSearchHistoryItem,
    handleSubmit,
    hasFocus,
    inputRef,
    insideRef,
    setValue,
    showSearchHistory,
    value,
  };
};
